import type { SerieDoAnalitoResponse } from '@/api/generated/model';
import { ehExterno, LEGENDA_DE_EXTERNO, rotuloDeExterno } from '@/features/historico/pontoExterno';
import { cn } from '@/shared/lib/cn';

/** Coordenadas do `viewBox`. O SVG escala sozinho; estes números são internos. */
const LARGURA = 720;
const ALTURA = 240;
const MARGEM = { topo: 16, direita: 16, base: 28, esquerda: 48 };

const AREA = {
  largura: LARGURA - MARGEM.esquerda - MARGEM.direita,
  altura: ALTURA - MARGEM.topo - MARGEM.base,
};

interface Props {
  serie: SerieDoAnalitoResponse;
}

/**
 * Evolução de um analito — SVG inline.
 *
 * ⚠️ **SVG à mão, e o repo AGORA tem biblioteca de gráfico.** Este componente
 * nasceu quando não havia nenhuma, e o argumento era que a V1 trazia o
 * `recharts` inteiro (~90 KB gzip) para desenhar uma linha com duas
 * referências. A Análise Comparativa passou a exigir quatro tipos de gráfico
 * interativos e trouxe o `recharts` — mas **sob `React.lazy`**, e é essa parte
 * que ainda decide o caso aqui: o histórico é rota do bundle principal, e uma
 * polyline com duas referências não justifica arrastar a dependência para
 * dentro dele. Migrar continua sendo opção aberta, não pendência; se for feito,
 * tem de vir com o carregamento adiado junto.
 *
 * ⚠️ **Nenhuma cor afirma que subir é ruim.** A V1 pintava toda alta de vermelho
 * — falso para metade dos analitos (hematócrito subindo é melhora). Aqui quem
 * decide a cor do ponto é a `interpretacao` que o servidor gravou, comparando
 * contra a faixa da espécie. Fora da faixa é âmbar; dentro é verde.
 *
 * ⚠️ **A FORMA do ponto diz de onde ele veio; a cor continua dizendo a
 * interpretação.** Medição da casa é círculo; valor de laudo externo é losango.
 * São dois eixos de informação, e pintar o externo de outra cor apagaria o
 * "fora da faixa" justamente no ponto em que mais se quer vê-lo.
 */
export function GraficoDeEvolucao({ serie }: Props) {
  const pontos = serie.pontos.filter(
    (ponto): ponto is typeof ponto & { valor: number } => ponto.valor !== null,
  );

  if (pontos.length < 2) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        {pontos.length === 0
          ? 'Nenhuma medição numérica para este exame.'
          : 'Uma única medição não desenha evolução — é preciso ao menos duas.'}
      </p>
    );
  }

  const valores = pontos.map((ponto) => ponto.valor);
  const candidatos = [...valores, serie.faixaMinimo, serie.faixaMaximo].filter(
    (valor): valor is number => valor !== null,
  );

  const bruto = { min: Math.min(...candidatos), max: Math.max(...candidatos) };
  // Série constante (todas as medições iguais) tem amplitude zero e dividiria
  // por zero na escala. Abre-se uma folga artificial para a linha ficar no meio.
  const amplitude = bruto.max - bruto.min || Math.abs(bruto.max) || 1;
  const folga = amplitude * 0.15;
  const escala = { min: bruto.min - folga, max: bruto.max + folga };

  const x = (indice: number) => MARGEM.esquerda + (indice / (pontos.length - 1)) * AREA.largura;

  const y = (valor: number) =>
    MARGEM.topo + AREA.altura - ((valor - escala.min) / (escala.max - escala.min)) * AREA.altura;

  const linha = pontos.map((ponto, i) => `${x(i)},${y(ponto.valor)}`).join(' ');

  const faixa =
    serie.faixaMinimo !== null || serie.faixaMaximo !== null
      ? {
          topo: y(serie.faixaMaximo ?? escala.max),
          base: y(serie.faixaMinimo ?? escala.min),
        }
      : null;

  const rotulos = [escala.max, (escala.max + escala.min) / 2, escala.min];
  const temExterno = pontos.some(ehExterno);

  return (
    <figure className="space-y-2">
      <svg
        viewBox={`0 0 ${LARGURA} ${ALTURA}`}
        className="w-full h-auto"
        role="img"
        aria-label={`Evolução de ${serie.nome}: ${pontos.length} medições, de ${formatar(
          valores[0]!,
        )} a ${formatar(valores[valores.length - 1]!)} ${serie.unidade ?? ''}`.trim()}
      >
        {/* Faixa de referência ATUAL do paciente. É contexto de leitura, não
            verdade histórica: os pontos guardam a interpretação da época. */}
        {faixa && (
          <rect
            x={MARGEM.esquerda}
            y={faixa.topo}
            width={AREA.largura}
            height={Math.max(faixa.base - faixa.topo, 1)}
            className="fill-emerald-500/10"
          />
        )}

        {rotulos.map((valor) => (
          <g key={valor}>
            <line
              x1={MARGEM.esquerda}
              x2={LARGURA - MARGEM.direita}
              y1={y(valor)}
              y2={y(valor)}
              className="stroke-border"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            <text
              x={MARGEM.esquerda - 8}
              y={y(valor) + 4}
              textAnchor="end"
              className="fill-muted-foreground text-[10px]"
            >
              {formatar(valor)}
            </text>
          </g>
        ))}

        <polyline
          points={linha}
          fill="none"
          className="stroke-brand"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {pontos.map((ponto, i) => {
          const classe = cn(
            'stroke-surface-raised',
            ponto.interpretacao === 'ABAIXO' || ponto.interpretacao === 'ACIMA'
              ? 'fill-amber-500'
              : 'fill-emerald-500',
          );
          const externo = rotuloDeExterno(ponto);
          const titulo = [
            `${formatarData(ponto.medidoEm)}: ${formatar(ponto.valor)} ${ponto.unidade ?? ''}`.trim(),
            externo,
          ]
            .filter(Boolean)
            .join(' — ');

          return (
            <g key={ponto.exameId}>
              {externo ? (
                // Losango: um quadrado girado 45° em torno do próprio centro.
                <rect
                  x={x(i) - 4.5}
                  y={y(ponto.valor) - 4.5}
                  width={9}
                  height={9}
                  transform={`rotate(45 ${x(i)} ${y(ponto.valor)})`}
                  className={classe}
                  strokeWidth={2}
                >
                  <title>{titulo}</title>
                </rect>
              ) : (
                <circle cx={x(i)} cy={y(ponto.valor)} r={4} className={classe} strokeWidth={2}>
                  <title>{titulo}</title>
                </circle>
              )}

              <text
                x={x(i)}
                y={ALTURA - 8}
                textAnchor="middle"
                className="fill-muted-foreground text-[10px]"
              >
                {formatarData(ponto.medidoEm)}
              </text>
            </g>
          );
        })}
      </svg>

      {(serie.faixaTexto || temExterno) && (
        <figcaption className="space-y-1 text-xs text-muted-foreground">
          {serie.faixaTexto && (
            <p>
              <span className="inline-block w-3 h-3 rounded-sm bg-emerald-500/20 align-middle mr-1.5" />
              Faixa de referência atual: {serie.faixaTexto}
            </p>
          )}
          {temExterno && (
            <p>
              <span
                aria-hidden="true"
                className="inline-block w-2.5 h-2.5 rotate-45 bg-muted-foreground/60 align-middle mr-2 ml-0.5"
              />
              {LEGENDA_DE_EXTERNO}
            </p>
          )}
        </figcaption>
      )}
    </figure>
  );
}

function formatar(valor: number): string {
  return valor.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

function formatarData(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}
